import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Iconify from '../../../theme/iconify/iconify';

import Quiz from '../quiz/learn/Quiz';
import Exam from '../quiz/exam/Exam';

function Subsection({
    subsection,
    index,
    course,
    activeSection,
    user,
    updateUser,
    setFindOutMorePrompt,
    moveSubsection,
    setEditingSubsection,
    setEditSubsectionIndex,
    setRemoveSubsectionModal,
    setSubsectionModal,
    setSubsectionTitle,
    setSubsectionContent,
    setQuiz,
    resetQuiz,
    setQuizModal,
    setRemoveQuizModal
}) {
    const { t } = useTranslation();

    const findOutMore = (section, subsection) => {
        if (!user) return;

        //  Data preparation
        setFindOutMorePrompt({
            course_id: course._id,
            section_id: section.id,
            subsection_id: subsection.id,
            user_prompt: `${t('course.chat.findOutMore.prompt')} "${subsection.title}".`
        });

        //  Open chat
        document.getElementById('mISLec_course_chat_button').click();

        //  Reset prompt
        setTimeout(() => {
            setFindOutMorePrompt(null);
        }, 100);
    }

    const reorder = (index, direction) => {
        const data = course;

        if (direction === 'up') {
            const temp = data.sections[activeSection].subsections[index - 1];
            data.sections[activeSection].subsections[index - 1] = data.sections[activeSection].subsections[index];
            data.sections[activeSection].subsections[index] = temp;
        } else if (direction === 'down') {
            const temp = data.sections[activeSection].subsections[index + 1];
            data.sections[activeSection].subsections[index + 1] = data.sections[activeSection].subsections[index];
            data.sections[activeSection].subsections[index] = temp;
        } else if (direction === 'start') {
            const temp = data.sections[activeSection].subsections[index];
            data.sections[activeSection].subsections.splice(index, 1);
            data.sections[activeSection].subsections.unshift(temp);
        } else if (direction === 'end') {
            const temp = data.sections[activeSection].subsections[index];
            data.sections[activeSection].subsections.splice(index, 1);
            data.sections[activeSection].subsections.push(temp);
        }

        moveSubsection(data);
    }

    return (
        <Grid container key={subsection.id} id={`mISLec_subsection_${subsection.id}`} className='mISLec_course_subsection' sx={{ p: 1, pb: subsection.quiz ? 0 : 1 }}>
            <Grid item xs={12} className='mISLec_course_heading'>
                <Grid container>
                    <Grid item xs={(user.role === 'admin' || user._id === course.user) ? 8 : 10}>
                        <Typography variant='h6'>
                            {subsection.content ? subsection.title : t(`course.modals.newQuiz.type.${subsection.quiz.type}`)}
                        </Typography>
                    </Grid>
                    <Grid item xs={(user.role === 'admin' || user._id === course.user) ? 4 : 2} sx={{ display: 'flex', justifyContent: 'right' }}>
                        {subsection.content && user.courses.find(c => c.id === course._id) &&
                            <Tooltip title={t('course.tooltips.findOutMore')} arrow>
                                <IconButton
                                    onClick={() => findOutMore(course.sections[activeSection], subsection)}
                                    sx={{
                                        pt: 0,
                                        pb: 0,
                                        pr: '4px',
                                        pl: '4px',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Iconify icon='heroicons:sparkles-solid' width={18} sx={{ transform: 'scale(-1, -1)' }} />
                                    <Typography variant='body2' sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: '700' }}>
                                        {t('course.chat.buttons.findOutMore')}
                                    </Typography>
                                </IconButton>
                            </Tooltip>
                        }
                        {subsection.quiz && subsection.quiz.type === 'learn' && user.role === 'student' &&
                            <Tooltip title={t('course.tooltips.reset')} arrow>
                                <IconButton
                                    onClick={() => resetQuiz(subsection.quiz, subsection.id)}
                                    sx={{
                                        pt: 0,
                                        pb: 0,
                                        pr: '4px',
                                        pl: '4px',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Iconify icon='codicon:debug-restart' width={18} />
                                    <Typography variant='body2' sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: '700' }}>
                                        {t('course.quiz.buttons.reset')}
                                    </Typography>
                                </IconButton>
                            </Tooltip>
                        }
                        {subsection.content && (user.role === 'admin' && user.courses.find(c => c.id === course._id)) &&
                            <Divider orientation='vertical' flexItem sx={{ m: '4px' }} />
                        }
                        {(user.role === 'admin' || user._id === course.user) &&
                            <>
                                <Tooltip title={t('course.tooltips.moveToStart')} arrow disabled={index === 0}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={index === 0}
                                            onClick={() => reorder(index, 'start')}
                                            sx={{
                                                pt: 0,
                                                pb: 0,
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='octicon:move-to-start-16' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('course.tooltips.moveUp')} arrow disabled={index === 0}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={index === 0}
                                            onClick={() => reorder(index, 'up')}
                                            sx={{
                                                pt: 0,
                                                pb: 0,
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='stash:arrow-up-solid' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('course.tooltips.moveDown')} arrow disabled={index === course.sections[activeSection].subsections.length - 1}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={index === course.sections[activeSection].subsections.length - 1}
                                            onClick={() => reorder(index, 'down')}
                                            sx={{
                                                pt: 0,
                                                pb: 0,
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='stash:arrow-down-solid' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('course.tooltips.moveToEnd')} arrow disabled={index === course.sections[activeSection].subsections.length - 1}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={index === course.sections[activeSection].subsections.length - 1}
                                            onClick={() => reorder(index, 'end')}
                                            sx={{
                                                pt: 0,
                                                pb: 0,
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='octicon:move-to-end-16' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Divider orientation='vertical' flexItem sx={{ m: '4px' }} />
                                <Tooltip title={subsection.quiz ? t('course.tooltips.editQuiz') : t('course.tooltips.editSubsection')} arrow>
                                    <IconButton
                                        onClick={() => {
                                            if (subsection.quiz) {
                                                setQuiz({ ...subsection.quiz, id: subsection.id });
                                                setEditSubsectionIndex(index);
                                                setTimeout(() => {
                                                    setQuizModal(true, subsection.quiz.type === 'exam');
                                                }, 100);
                                            }

                                            if (subsection.content) {
                                                setSubsectionTitle(subsection.title);
                                                setSubsectionContent(subsection.content);
                                                setEditingSubsection(true);
                                                setEditSubsectionIndex(index);
                                                setTimeout(() => {
                                                    setSubsectionModal(true);
                                                }, 100);
                                            }
                                        }}
                                        sx={{
                                            pt: 0,
                                            pb: 0,
                                            pr: '4px',
                                            pl: '4px',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Iconify icon='eva:edit-outline' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={subsection.quiz ? t('course.tooltips.removeQuiz') : t('course.tooltips.removeSubsection')} arrow>
                                    <IconButton
                                        color='error'
                                        onClick={() => {
                                            if (subsection.quiz) setRemoveQuizModal(true);
                                            if (subsection.content) setRemoveSubsectionModal(true);
                                            setEditSubsectionIndex(index);
                                        }}
                                        sx={{
                                            pt: 0,
                                            pb: 0,
                                            pr: '5px',
                                            pl: '5px',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Iconify icon='eva:trash-outline' width={17} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        }
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                {subsection.content && (
                    <div dangerouslySetInnerHTML={{ __html: subsection.content }} />
                )}
                {subsection.quiz && subsection.quiz.type === 'learn' && (
                    <Quiz user={user} updateUser={updateUser} quiz={subsection.quiz} courseId={course?._id} sectionId={course?.sections[activeSection]?.id} subsectionId={subsection.id} />
                )}
                {subsection.quiz && subsection.quiz.type === 'exam' && (
                    <Exam user={user} updateUser={updateUser} quiz={subsection.quiz} courseId={course?._id} sectionId={course?.sections[activeSection]?.id} subsectionId={subsection.id} />
                )}
            </Grid>
        </Grid>
    );
}

export default Subsection;